/**
 * Payment Modal Store using React Hooks
 * Manages the state of the payment selection modal window
 *
 * Note: This implementation uses local component state for simplicity.
 * For complex state management across multiple screens, consider
 * migrating to Redux store in store/slices/paymentModalSlice.ts
 */
import { useCallback, useState } from 'react'

import { SpecialCondition } from '@/entities/Payments'

/**
 * Payment modal hook
 * Handles opening/closing modal and tracking active condition
 *
 * @example
 * const { isOpen, activeCondition, open, close } = usePaymentModal()
 */
export const usePaymentModal = () => {
	const [isOpen, setIsOpen] = useState(false)
	const [activeCondition, setActiveCondition] = useState<SpecialCondition | null>(null)

	const open = useCallback((condition: SpecialCondition) => {
		setActiveCondition(condition)
		setIsOpen(true)
	}, [])

	const close = useCallback(() => {
		setIsOpen(false)
	}, [])

	const reset = useCallback(() => {
		setIsOpen(false)
		setActiveCondition(null)
	}, [])

	return {
		isOpen,
		activeCondition,
		open,
		close,
		reset
	}
}
